import { IDashboard } from '@/interfaces/powerMarkets/dashboard/IDashboard.ts';
import { IDateObject } from '@/interfaces/powerMarkets/IDateObject.ts';

/**
 * TEMP dual-site compat for old Enact userDashboard overrides.
 * Delete this file + call sites when old Enact is retired.
 *
 * Spark canonical: dateOverrides.chart
 * Enact legacy: fromToDateOverride + root timezone / periodsPerDay
 */

const DASHBOARD_OVERRIDE_MIRROR_VERSION = 1;

export type DashboardOverrideMirrorState = {
    version: typeof DASHBOARD_OVERRIDE_MIRROR_VERSION;
    date: boolean;
    timezone: boolean;
    periodsPerDay: boolean;
};

/** Enact also stores these on the view root (not typed on Spark IDashboard). */
export type LegacyDashboardRootSettings = {
    timezone?: string | null;
    periodsPerDay?: number | null;
    dashboardOverrideMirror?: DashboardOverrideMirrorState;
};

export type IDashboardWithDateOverrideMirrors = IDashboard & LegacyDashboardRootSettings;

export type ApplyDashboardDateOverrideMirrorsOptions = {
    /**
     * When true (load only), reconcile Enact's independently stored fields into Spark.
     * Must stay false on save so stale legacy values cannot resurrect cleared Spark settings.
     */
    hydrateFromLegacy?: boolean;
};

function hasDateOverride (dateOverride: IDateObject | undefined): dateOverride is IDateObject {
    return dateOverride?.from !== undefined && dateOverride.to !== undefined;
}

function hasTimezoneOverride (dateOverride: IDateObject | undefined): boolean {
    return typeof dateOverride?.timezone === 'string' && dateOverride.timezone.length > 0;
}

function hasPeriodsPerDayOverride (dateOverride: IDateObject | undefined): boolean {
    return dateOverride?.periodsPerDay !== null && dateOverride?.periodsPerDay !== undefined;
}

function getDateFields (dateOverride: IDateObject): IDateObject {
    const dateFields = { ...dateOverride };
    delete dateFields.timezone;
    delete dateFields.periodsPerDay;
    return dateFields;
}

function getTimezoneAndPeriodFields (dateOverride: IDateObject): IDateObject {
    const fields: IDateObject = {};
    if ('timezone' in dateOverride) fields.timezone = dateOverride.timezone;
    if ('periodsPerDay' in dateOverride) fields.periodsPerDay = dateOverride.periodsPerDay;
    return fields;
}

function setChartOverride (dashboard: IDashboardWithDateOverrideMirrors, chart: IDateObject): void {
    if (Object.keys(chart).length > 0) {
        dashboard.dateOverrides = {
            ...dashboard.dateOverrides,
            chart,
        };
        return;
    }

    if (dashboard.dateOverrides) delete dashboard.dateOverrides.chart;
}

function hydrateChartFromLegacy (dashboard: IDashboardWithDateOverrideMirrors): void {
    const previousMirror = dashboard.dashboardOverrideMirror?.version === DASHBOARD_OVERRIDE_MIRROR_VERSION
        ? dashboard.dashboardOverrideMirror
        : undefined;
    let chart: IDateObject = { ...dashboard.dateOverrides?.chart };

    if (dashboard.fromToDateOverride != null) {
        chart = {
            ...getDateFields(dashboard.fromToDateOverride),
            ...getTimezoneAndPeriodFields(chart),
        };
    } else if (previousMirror?.date) {
        chart = getTimezoneAndPeriodFields(chart);
    }

    if (dashboard.timezone != null) {
        chart.timezone = dashboard.timezone;
    } else if (previousMirror?.timezone) {
        delete chart.timezone;
    }

    if (dashboard.periodsPerDay != null) {
        chart.periodsPerDay = dashboard.periodsPerDay;
    } else if (previousMirror?.periodsPerDay) {
        delete chart.periodsPerDay;
    }

    setChartOverride(dashboard, chart);
}

function mirrorChartToLegacy (dashboard: IDashboardWithDateOverrideMirrors): void {
    const chart = dashboard.dateOverrides?.chart;
    const hasDate = hasDateOverride(chart);
    const hasTimezone = hasTimezoneOverride(chart);
    const hasPeriodsPerDay = hasPeriodsPerDayOverride(chart);

    dashboard.fromToDateOverride = hasDate ? getDateFields(chart) : null;

    if (hasTimezone && chart) dashboard.timezone = chart.timezone;
    else delete dashboard.timezone;

    if (hasPeriodsPerDay && chart) dashboard.periodsPerDay = chart.periodsPerDay;
    else delete dashboard.periodsPerDay;

    dashboard.dashboardOverrideMirror = {
        version: DASHBOARD_OVERRIDE_MIRROR_VERSION,
        date: hasDate,
        timezone: hasTimezone,
        periodsPerDay: hasPeriodsPerDay,
    };
}

/**
 * Reconciles Enact fields on load, then mirrors Spark's independent override fields for Enact.
 * Mutates and returns the same object.
 */
export function applyDashboardDateOverrideMirrors (
    dashboard: IDashboard,
    options: ApplyDashboardDateOverrideMirrorsOptions = {},
): IDashboardWithDateOverrideMirrors {
    const mirrored = dashboard as IDashboardWithDateOverrideMirrors;

    if (options.hydrateFromLegacy === true) hydrateChartFromLegacy(mirrored);
    mirrorChartToLegacy(mirrored);

    return mirrored;
}
